import React from 'react';
import { useNavigate } from 'react-router-dom';
import ConfigRoutes from 'app/routing/ConfigRoutes';

interface TenStatisticsProps {
  reportData: any;
  filters?: {
    selectedFormats?: string[];
    selectedProgramme?: string;
    selectedProgrammeLabel?: string;
    selectedBrand?: string;
    selectedSupporters?: string[];
    selectedComparison?: string;
  } | null;
}

const TenStatistics: React.FC<TenStatisticsProps> = ({ reportData, filters }) => {
  const navigate = useNavigate();

  const formatNumber = (num: number) => {
    return Math.round(num ?? 0).toLocaleString();
  };

  const styles = {
    section: {
      marginBottom: '24px',
      border: '1px solid #e0e0e0',
      borderRadius: '4px',
      overflow: 'hidden',
      backgroundColor: '#fff',
      fontFamily: 'Arial, sans-serif'
    },
    header: {
      backgroundColor: '#c75a3a',
      color: 'white',
      padding: '12px 16px',
      fontWeight: 'bold',
      fontSize: '18px',
      margin: 0
    },
    subFormatHeader: {
      backgroundColor: '#477995',
      color: 'white',
      padding: '10px 16px',
      fontWeight: 'bold',
      fontSize: '16px',
      margin: 0
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse' as const,
    },
    row: {
      borderBottom: '1px solid #f0f0f0',
    },
    label: {
      padding: '12px 16px',
      fontSize: '15px',
      color: '#333',
      fontWeight: '600',
      width: '70%'
    },
    value: {
      padding: '12px 16px',
      fontSize: '15px',
      color: '#333',
      fontWeight: 'bold',
      textAlign: 'right' as const,
      width: '30%'
    }
  };

  const renderMetrics = (data: any) => {
    if (!data) {
      return (
        <div className="text-center py-4">
          No data available
        </div>
      );
    }

    return (
      <table style={styles.table}>
        <tbody>
          <tr style={styles.row}>
            <td style={styles.label}>
              Mean Average Number of Engagements
            </td>
            <td style={styles.value}>
              {formatNumber(data?.mean || 0)}
            </td>
          </tr>

          <tr style={styles.row}>
            <td style={styles.label}>
              Median Average Number of Engagements
            </td>
            <td style={styles.value}>
              {formatNumber(data?.median || 0)}
            </td>
          </tr>

          <tr style={styles.row}>
            <td style={styles.label}>
              Maximum TEN
            </td>
            <td style={styles.value}>
              {formatNumber(data?.maximum || 0)}
            </td>
          </tr>

          <tr
            style={{
              ...styles.row,
              borderBottom: 'none'
            }}
          >
            <td style={styles.label}>
              Minimum TEN
            </td>
            <td style={styles.value}>
              {formatNumber(data?.minimum || 0)}
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderFormatData = (data: any) => {
    if (!data) {
      return (
        <div className="text-center py-4">
          No data available
        </div>
      );
    }

    const resultsArray = Array.isArray(data)
      ? data
      : [data];

    return (
      <div className="formats-container">
        {resultsArray.map((formatResult: any, index: number) => {
          const subFormats = Array.isArray(formatResult.subFormats)
            ? formatResult.subFormats
            : [];

          return (
            <div
              key={`${formatResult.title || formatResult.format || 'selection'}-${index}`}
              style={styles.section}
            >
              {/* FORMAT */}
              <div style={styles.header}>
                {formatResult.title || formatResult.format || 'Selection'}
                {' '}
                (
                {formatResult.data?.count ?? 0}
                {' '}
                {Number(formatResult.data?.count) === 1
                  ? 'programme'
                  : 'programmes'}
                )
              </div>

              {renderMetrics(formatResult.data)}

              {/* SUB-FORMATS */}
              {subFormats.map((subFormat: any, subIndex: number) => (
                <React.Fragment
                  key={`${subFormat.value || subFormat.title}-${subIndex}`}
                >
                  <div style={styles.subFormatHeader}>
                    {subFormat.title || subFormat.value || 'Sub-Format'}
                    {' '}
                    (
                    {subFormat.data?.count ?? 0}
                    {' '}
                    {Number(subFormat.data?.count) === 1
                      ? 'programme'
                      : 'programmes'}
                    )
                  </div>

                  {renderMetrics(subFormat.data)}
                </React.Fragment>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className='container-fluid mt-4'>
      <div className='row'>
        <div className='col-12'>
          <div
            className='card shadow-sm border-dark'
            style={{ borderWidth: '2px' }}
          >

            <div className='card-header text-white d-flex justify-content-between align-items-center p-3'>
              <h4 className='mb-0 fw-bold text-uppercase'>
                TEN Dashboard
              </h4>

              <button
                className='btn btn-primary'
                onClick={() =>
                  navigate(ConfigRoutes.admin.tenDashboard.principal)
                }
              >
                <i className='bi bi-arrow-left me-1'></i>
                {' '}
                New Report
              </button>
            </div>

            <div className='card-body px-5 pb-5'>
              <div className='row mb-5 border-bottom pb-4'>
                <div className='col-md-12'>
                  <h5 className='text-muted mb-3'>
                    Search Configuration
                  </h5>

                  <div className='d-flex flex-wrap gap-2'>
                    {filters?.selectedFormats?.map((f: string) => (
                      <span
                        key={f}
                        className='badge bg-light text-dark border p-2'
                      >
                        Format:{' '}
                        {f.charAt(0).toUpperCase() +
                          f.slice(1).toLowerCase()}
                      </span>
                    ))}

                    {filters?.selectedProgramme && (
                      <span className='badge bg-light text-dark border p-2'>
                        Prog:{' '}
                        {filters.selectedProgrammeLabel ||
                          filters.selectedProgramme}
                      </span>
                    )}

                    {filters?.selectedBrand && (
                      <>
                        {filters.selectedBrand === 'both' ? (
                          <>
                            <span className='badge bg-light text-dark border p-2'>
                              Company: COR2ED
                            </span>

                            <span className='badge bg-light text-dark border p-2'>
                              Company: Medical Affairs
                            </span>
                          </>
                        ) : (
                          <span className='badge bg-light text-dark border p-2'>
                            Company:{' '}
                            {filters.selectedBrand === 'medAffairs'
                              ? 'Medical Affairs'
                              : 'COR2ED'}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div
                className='title-supporter'
                style={{
                  color: '#477995',
                  fontWeight: 'bold',
                  textDecoration: 'underline',
                  marginBottom: '5px'
                }}
              >
                TEN Statistics
              </div>

              <div
                className='subtitle-supporter mb-4'
                style={{
                  color: '#666'
                }}
              >
                Statistics calculated based on your selection
              </div>

              <div className='formats-list'>
                {renderFormatData(reportData)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { TenStatistics };